// PERMANENT: Privacy Policy — required for affiliate/compliance coverage. Do not remove.
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const NAVY = "#1B2F5E";
const GOLD = "#C9A96E";

export default function PrivacyPolicyPage() {
  const updated = "August 2026";
  return (
    <div className="min-h-screen topo-bg" data-testid="privacy-policy-page">
      <SeoHead
        title="Privacy Policy"
        description="How Summary Maps LLC collects, uses, and protects your data — newsletter, analytics, chat, and affiliate links."
      />
      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-20 sm:pb-24" style={{ maxWidth: 720 }}>
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-brand mb-8" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to the journal
        </Link>

        <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: GOLD }}>Legal</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold leading-tight" style={{ color: NAVY }}>Privacy Policy</h1>
        <p className="mt-4 text-sm" style={{ color: "#5C5344" }}>Effective date: {updated}</p>

        <div style={{ color: "#2D3748", fontSize: "1.0625rem", lineHeight: 1.75 }} className="mt-8 space-y-5">
          <p>
            Summary Maps LLC ("we", "us", or "Summary Maps") operates SummaryMaps Journal at
            summarymapsjournal.com. This policy explains what personal information we collect, how we
            use it, and the choices you have. If you have any questions, contact us at{" "}
            <a href="mailto:contact@summarymaps.com" style={{ color: GOLD, textDecoration: "underline" }}>contact@summarymaps.com</a>.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>1. Information we collect</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong style={{ color: NAVY }}>Email addresses</strong> submitted via the newsletter signup form.</li>
            <li><strong style={{ color: NAVY }}>Cookies</strong> — small text files set by our site and by third parties (see Section 3).</li>
            <li><strong style={{ color: NAVY }}>Google Analytics 4</strong> data — pageviews, device type, referral source, approximate location, and other anonymized usage signals.</li>
            <li><strong style={{ color: NAVY }}>Meta Pixel</strong> data — anonymized event data (pageviews, actions) used for advertising measurement and audience building on Facebook and Instagram.</li>
            <li><strong style={{ color: NAVY }}>ManyChat</strong> — if you interact with the on-site chat widget, the messages you send are processed by ManyChat on our behalf.</li>
            <li><strong style={{ color: NAVY }}>Server logs</strong> — standard request data (IP, user-agent, timestamp) kept briefly for security and debugging.</li>
          </ul>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>2. How we use your information</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>To send our newsletter to subscribers who opted in.</li>
            <li>To understand which essays and topics our audience finds valuable.</li>
            <li>To improve site performance, content, and design.</li>
            <li>To measure advertising reach on Meta properties (Facebook/Instagram).</li>
            <li>To detect and prevent abuse.</li>
          </ul>
          <p>We do not sell your personal information.</p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>3. Third-party services</h2>
          <p>We do not currently show display advertising on SummaryMaps Journal. The services that actually run on the site are:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong style={{ color: NAVY }}>Google Analytics (GA4)</strong> — website analytics. See{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: GOLD, textDecoration: "underline" }}>Google's privacy policy</a>.
            </li>
            <li><strong style={{ color: NAVY }}>Meta Pixel (Facebook/Instagram)</strong> — advertising measurement and audience building. See{" "}
              <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" style={{ color: GOLD, textDecoration: "underline" }}>Meta's privacy policy</a>.
            </li>
            <li><strong style={{ color: NAVY }}>ManyChat</strong> — powers the on-site chat widget.</li>
            <li><strong style={{ color: NAVY }}>Resend</strong> — delivers our newsletter emails. See{" "}
              <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: GOLD, textDecoration: "underline" }}>Resend's privacy policy</a>.
            </li>
            <li><strong style={{ color: NAVY }}>Cloudflare</strong> — DNS and traffic protection.</li>
            <li><strong style={{ color: NAVY }}>MongoDB Atlas</strong> — stores our articles and subscriber list.</li>
          </ul>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>4. Affiliate links</h2>
          <p>
            Some book links on the Journal go to Amazon (as an Amazon Associate we earn from qualifying purchases)
            and Bookshop.org. When you click those links, Amazon and Bookshop.org may set their own cookies, and
            their own privacy policies apply.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>5. Cookies</h2>
          <p>
            We do not set first-party cookies beyond what is required for the site to function. The third parties
            listed above may set cookies for analytics and measurement. You can manage cookies through your
            browser settings — blocking cookies will not break the site.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>6. Your rights</h2>
          <p>You may at any time:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong style={{ color: NAVY }}>Unsubscribe</strong> from the newsletter using the link in any email, or by writing to us.</li>
            <li><strong style={{ color: NAVY }}>Request a copy</strong> of the data we hold about you.</li>
            <li><strong style={{ color: NAVY }}>Request deletion</strong> of your email and any associated records.</li>
          </ul>
          <p>
            If you are in the EU/UK you have additional rights under GDPR. If you are in California you have
            additional rights under the CCPA. To exercise any of these, email{" "}
            <a href="mailto:contact@summarymaps.com" style={{ color: GOLD, textDecoration: "underline" }}>contact@summarymaps.com</a>.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>7. Data retention</h2>
          <p>
            Newsletter subscriber emails are kept until you unsubscribe or request deletion. Analytics data is
            retained for up to 14 months. Server logs are kept for up to 30 days.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>8. Children</h2>
          <p>
            The Journal is not directed at children under 13 and we do not knowingly collect personal
            information from them. If you believe a child has subscribed, contact us and we will delete the
            record.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>9. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. The "Effective date" at the top will reflect the
            latest version. Material changes will be announced at the top of the newsletter.
          </p>

          <h2 className="font-serif" style={{ color: NAVY, fontSize: "1.5rem", fontWeight: 700, marginTop: "2.5rem", marginBottom: "0.75rem" }}>10. Contact</h2>
          <p>
            Summary Maps LLC<br />
            Email: <a href="mailto:contact@summarymaps.com" style={{ color: GOLD, textDecoration: "underline" }}>contact@summarymaps.com</a>
          </p>
        </div>
      </main>
    </div>
  );
}
